import { useEffect } from "react";
import { useLocation } from "wouter";
import {
  canonicalUrlForPath,
  DEFAULT_OG_IMAGE,
  metaForPath,
} from "@/lib/pageMeta";

function upsertMeta(
  selector: string,
  create: () => HTMLElement,
  apply: (el: HTMLElement) => void,
) {
  let el = document.head.querySelector(selector) as HTMLElement | null;
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  apply(el);
}

/**
 * Updates document title and meta tags per public route (hash-based SPA).
 */
export function PageMeta() {
  const [location] = useLocation();
  const meta = metaForPath(location);
  const canonical = canonicalUrlForPath(meta.path);

  useEffect(() => {
    document.title = meta.title;

    upsertMeta(
      'meta[name="description"]',
      () => {
        const m = document.createElement("meta");
        m.setAttribute("name", "description");
        return m;
      },
      (el) => el.setAttribute("content", meta.description),
    );

    upsertMeta(
      'link[rel="canonical"]',
      () => {
        const l = document.createElement("link");
        l.setAttribute("rel", "canonical");
        return l;
      },
      (el) => el.setAttribute("href", canonical),
    );

    const ogPairs: Record<string, string> = {
      "og:title": meta.title,
      "og:description": meta.description,
      "og:url": canonical,
      "og:type": "website",
      "og:image": DEFAULT_OG_IMAGE,
    };

    for (const [property, content] of Object.entries(ogPairs)) {
      upsertMeta(
        `meta[property="${property}"]`,
        () => {
          const m = document.createElement("meta");
          m.setAttribute("property", property);
          return m;
        },
        (el) => el.setAttribute("content", content),
      );
    }

    const twitterPairs: Record<string, string> = {
      "twitter:card": "summary_large_image",
      "twitter:title": meta.title,
      "twitter:description": meta.description,
      "twitter:image": DEFAULT_OG_IMAGE,
    };

    for (const [name, content] of Object.entries(twitterPairs)) {
      upsertMeta(
        `meta[name="${name}"]`,
        () => {
          const m = document.createElement("meta");
          m.setAttribute("name", name);
          return m;
        },
        (el) => el.setAttribute("content", content),
      );
    }
  }, [meta.title, meta.description, canonical]);

  return null;
}
